<template>
  <!-- Standalone confirm (UX R-6 / UI_SPEC C3.4): the pre-existing AI button
       needs the same 2-button confirm even when the panel is absent or
       collapsed, so it renders the confirm on its own, under the textarea. -->
  <div
    v-if="variant === 'confirm-only'"
    class="sg-confirm is-standalone"
    dir="rtl"
    role="group"
    @keydown.esc.stop.prevent="$emit('confirm-cancel')"
  >
    <p class="sg-confirm-msg" aria-live="polite">{{ copy.replaceConfirm }}</p>
    <div class="sg-confirm-row">
      <button ref="confirmPrimary" type="button" class="sg-btn is-primary" @click="$emit('confirm-accept')">{{ copy.replace }}</button>
      <button type="button" class="sg-btn is-ghost" @click="$emit('confirm-cancel')">{{ copy.cancel }}</button>
    </div>
  </div>

  <div
    v-else-if="view.state !== 'off' && view.state !== 'skipped-silent'"
    :id="panelId"
    class="sg-panel"
    :class="{ 'is-confirming': view.state === 'confirm-replace' }"
    dir="rtl"
  >
    <div v-if="showCard" class="sg-card">
      <div class="sg-caption">
        <span>{{ copy.caption }}</span>
        <button type="button" class="sg-link" @click="setExpanded(false)">
          {{ copy.collapse }} <i class="fas fa-chevron-up" aria-hidden="true"></i>
        </button>
      </div>
      <p v-if="reuseLabel" class="sg-label">{{ reuseLabel }}</p>
      <p v-if="partialLabel" class="sg-label">{{ partialLabel }}</p>
      <div class="sg-divider"></div>
      <!-- Server-built text: moustache interpolation only, CSS bidi isolation, never raw-HTML rendering. -->
      <div ref="text" class="sg-text" :class="{ 'is-full': textExpanded }" tabindex="0">{{ view.suggestedText }}</div>
      <button v-if="textClamped" type="button" class="sg-link sg-text-more" @click="textExpanded = !textExpanded">
        {{ textExpanded ? copy.collapse : copy.showAll }}
      </button>
    </div>

    <div v-if="showActions" class="sg-actions" dir="rtl">
      <button type="button" class="sg-btn is-primary" @click="onUseClick">{{ copy.use }}</button>
      <button type="button" class="sg-btn is-secondary" @click="$emit('append')">{{ copy.append }}</button>
      <button
        type="button"
        class="sg-link"
        :aria-disabled="canRegenerate ? 'false' : 'true'"
        @click="onRegenerate"
      >{{ copy.regenerate }}</button>
    </div>

    <div
      v-if="view.state === 'confirm-replace'"
      class="sg-confirm"
      dir="rtl"
      role="group"
      @keydown.esc.stop.prevent="cancelConfirm"
    >
      <p class="sg-confirm-msg" aria-live="polite">{{ copy.replaceConfirm }}</p>
      <div class="sg-confirm-row">
        <button ref="confirmPrimary" type="button" class="sg-btn is-primary" @click="acceptConfirm">{{ copy.replace }}</button>
        <button type="button" class="sg-btn is-ghost" @click="cancelConfirm">{{ copy.cancel }}</button>
      </div>
    </div>

    <div v-if="showRow" class="sg-row" :class="{ 'is-wrap': view.state === 'gave-up', 'is-ready': isReadyRow }">
      <i v-if="isAppliedRow" class="fas fa-check sg-check" aria-hidden="true"></i>
      <span v-else class="sg-marker" :class="{ 'is-filled': isReadyRow }"></span>

      <span class="sg-text-line">
        {{ rowText }}<template v-if="view.state === 'debounced'"><bdi>{{ debounceSeconds }}</bdi> {{ copy.debouncedSuffix }}</template>
      </span>

      <button v-if="isReadyRow" type="button" class="sg-link" @click="setExpanded(true)">{{ copy.show }}</button>
      <button v-if="isAppliedRow" type="button" class="sg-link" @click="$emit('undo')">{{ copy.undo }}</button>
      <button
        v-if="showReopenLink"
        type="button"
        class="sg-link"
        @click="setExpanded(true)"
      >{{ copy.showSuggestion }}</button>
      <button
        v-if="showRegenerateLink"
        type="button"
        class="sg-link"
        :aria-disabled="canRegenerate ? 'false' : 'true'"
        @click="onRegenerate"
      >{{ copy.regenerate }}</button>
    </div>
  </div>
</template>

<script>
import { MODE_LABELS, CONFIRM_MIN_CHARS } from '@/store/modules/cvTextArtifacts'

// Suggestion panel (UI_SPEC C3 / UX §5.2, §7.2).
//
// Height contract: every non-expanded state is exactly one 28px row (gave-up
// excepted, which wraps to two lines), so `ready` arriving while she types
// changes the page height by 0px. Expanded is user-initiated and capped at
// 210px (card 168 + gap 8 + action row 34).
//
// This component NEVER writes `manual_text`. It emits `use` / `append` / `undo`
// and the owning modal performs the write — that is what keeps the write
// invariant (FR-7 AC-1) enforceable by the lint and by FE-7/FE-11.
const COPY = {
  caption: 'הצעה אוטומטית לטקסט המלווה',
  pending: 'מכינים הצעת טקסט…',
  pendingLong: 'עדיין מכינים — אפשר לשלוח בלי לחכות',
  readyCollapsed: 'הצעת טקסט מוכנה —',
  // One half landed, the other is still coming. `readyCollapsed` alone claimed
  // the whole suggestion was done.
  readyCollapsedPartial: 'הצעת טקסט מוכנה (חלק נוסף בהכנה) —',
  regenPartial: 'מכינים מחדש את החלק החסר…',
  applied: 'הטקסט הוזן לשדה',
  undoDone: 'הוחזר הטקסט הקודם',
  failed: 'לא הצלחנו להכין הצעה — אפשר לכתוב ידנית',
  gaveUp: 'הפסקנו לבדוק — ההכנה נמשכת מעבר לזמן הצפוי. אפשר לכתוב ידנית או לנסות שוב.',
  skipped: 'לא הוכנה הצעה הפעם — אפשר לכתוב ידנית',
  debouncedPrefix: 'אפשר ליצור מחדש בעוד ',
  debouncedSuffix: 'שניות',
  use: 'השתמשי בטקסט',
  append: 'הוסיפי בסוף',
  regenerate: 'צרי מחדש',
  show: 'הצג',
  showSuggestion: 'הצג הצעה',
  showAll: 'הצג הכל',
  collapse: 'כווץ',
  undo: 'בטלי',
  replaceConfirm: 'להחליף את הטקסט שכתבת? הטקסט הקיים יימחק.',
  replace: 'החלפה',
  cancel: 'ביטול'
}

export default {
  name: 'CvSuggestionPanel',
  props: {
    offerId: {
      type: [Number, String],
      required: true
    },
    mode: {
      type: String,
      default: 'normal'
    },
    // Length of the recruiter's own typed text — drives the >=20-char confirm.
    typedLength: {
      type: Number,
      default: 0
    },
    variant: {
      type: String,
      default: 'panel'
    }
  },
  data () {
    return {
      copy: COPY,
      now: Date.now(),
      textExpanded: false,
      textClamped: false,
      clock: null
    }
  },
  computed: {
    panelId () {
      // Per-offer id (UI reviewer issue 3) — aria-controls on the cue matches.
      return `sg-panel-${this.offerId}`
    },
    view () {
      return this.$store.getters['cvTextArtifacts/panelView'](this.offerId, this.mode, this.now)
    },
    envelope () {
      return this.$store.getters['cvTextArtifacts/envelopeFor'](this.offerId, this.mode)
    },
    // `applied-expanded` / `undo-done-expanded` are the re-opened suggestion:
    // the card comes back with the status row under it, so `הצג הצעה` has
    // somewhere to go and `כווץ` gets back to the quiet row.
    showCard () {
      return [
        'ready-expanded', 'confirm-replace', 'regenerating-partial',
        'applied-expanded', 'undo-done-expanded'
      ].includes(this.view.state)
    },
    // The re-opened views keep the action row so she can re-apply — UX §7.2 E-1:
    // the suggestion is never discarded.
    showActions () {
      return [
        'ready-expanded', 'regenerating-partial', 'applied-expanded', 'undo-done-expanded'
      ].includes(this.view.state)
    },
    showRow () {
      if (['regenerating-partial', 'applied-expanded', 'undo-done-expanded'].includes(this.view.state)) return true
      return !this.showCard
    },
    isReadyRow () {
      return ['ready-collapsed', 'ready-collapsed-partial'].includes(this.view.state)
    },
    isAppliedRow () {
      return ['applied-collapsed', 'applied-expanded'].includes(this.view.state)
    },
    // Only from a COLLAPSED applied/undo row — when the card is already open the
    // link would be a no-op, which is the dead end this whole state pair fixes.
    //
    // `canReopen` is the second half of the same rule: the collapsed row is also
    // what the derivation returns when there is NO ready text (the cross-mode
    // unsafe-switch window, a purged key), and there the link expands into
    // nothing for as long as that window lasts. A link that cannot open anything
    // must not be on screen.
    showReopenLink () {
      return ['applied-collapsed', 'undo-done'].includes(this.view.state) && this.view.canReopen === true
    },
    showRegenerateLink () {
      return ['failed', 'gave-up', 'debounced'].includes(this.view.state)
    },
    canRegenerate () {
      return this.view.canRegenerate === true
    },
    debounceSeconds () {
      return Math.ceil(this.view.debounceRemainingMs / 1000)
    },
    // R-4: the SERVER decides whether this envelope is a reuse and what to call
    // it (Cv::TextArtifactService::REUSE_LABEL), and it is the same server that
    // decided the reuse was legal in the first place. Rebuilding the sentence
    // here from a per-kind field meant the label and the reuse decision could
    // disagree — and, until the derivation was fixed, the label simply never
    // appeared. A local fallback covers the per-kind case the server label
    // does not name.
    reuseLabel () {
      if (this.view.reuseLabel) return this.view.reuseLabel
      if (!this.view.reusedFromMode) return ''
      const label = MODE_LABELS[this.view.reusedFromMode] || this.view.reusedFromMode
      return `נוצר עבור שליחה ${label} — ניתן לשימוש גם כאן`
    },
    partialLabel () {
      const parts = this.view.parts || []
      if (parts.length !== 1) return ''
      return parts[0] === 'professional_notes' ? 'כולל הערות מקצועיות בלבד' : 'כולל התרשמות אישית בלבד'
    },
    modeLabel () {
      return MODE_LABELS[this.mode] || this.mode
    },
    rowText () {
      switch (this.view.state) {
        case 'pending':
        case 'regenerating':
          return COPY.pending
        case 'pending-long':
          return COPY.pendingLong
        case 'mode-switch-pending':
          return `מכינים הצעת טקסט מותאמת לשליחת ${this.modeLabel}…`
        case 'ready-collapsed':
          return COPY.readyCollapsed
        case 'ready-collapsed-partial':
          return COPY.readyCollapsedPartial
        case 'regenerating-partial':
          return COPY.regenPartial
        case 'applied-collapsed':
        case 'applied-expanded':
          return COPY.applied
        case 'undo-done':
        case 'undo-done-expanded':
          return COPY.undoDone
        case 'failed':
          return COPY.failed
        case 'gave-up':
          return COPY.gaveUp
        case 'skipped-visible':
          return COPY.skipped
        case 'debounced':
          return COPY.debouncedPrefix
        default:
          return ''
      }
    }
  },
  watch: {
    'view.state' (next) {
      if (next === 'pending' || next === 'pending-long' || next === 'mode-switch-pending') {
        this.$store.dispatch('cvTextArtifacts/markPendingShown', this.offerId)
      }
      if (next === 'confirm-replace') {
        this.$nextTick(this.focusConfirm)
      }
      this.$nextTick(this.measureText)
    },
    'view.suggestedText' () {
      this.$nextTick(this.measureText)
    }
  },
  mounted () {
    // One shared 1s clock: it drives the pending -> pending-long copy change and
    // the debounce countdown, so neither needs its own timer.
    this.clock = setInterval(() => { this.now = Date.now() }, 1000)
    if (this.variant === 'confirm-only') {
      this.$nextTick(this.focusConfirm)
      return
    }
    if (['pending', 'pending-long', 'mode-switch-pending'].includes(this.view.state)) {
      this.$store.dispatch('cvTextArtifacts/markPendingShown', this.offerId)
    }
    this.$nextTick(this.measureText)
  },
  beforeDestroy () {
    if (this.clock) clearInterval(this.clock)
    this.clock = null
  },
  methods: {
    setExpanded (value) {
      this.$store.dispatch('cvTextArtifacts/setExpanded', { offerId: this.offerId, value: value })
    },
    onUseClick () {
      if (this.typedLength >= CONFIRM_MIN_CHARS) {
        this.$store.dispatch('cvTextArtifacts/setConfirmOpen', { offerId: this.offerId, value: true, kind: 'use' })
        return
      }
      this.$emit('use')
    },
    acceptConfirm () {
      this.$store.dispatch('cvTextArtifacts/setConfirmOpen', { offerId: this.offerId, value: false })
      this.$emit('use')
    },
    cancelConfirm () {
      this.$store.dispatch('cvTextArtifacts/setConfirmOpen', { offerId: this.offerId, value: false })
      this.$emit('confirm-cancel')
    },
    focusConfirm () {
      const btn = this.$refs.confirmPrimary
      if (btn && btn.focus) btn.focus()
    },
    onRegenerate () {
      if (!this.canRegenerate) return
      this.$emit('regenerate', this.failedKind())
    },
    // R-9: regenerate targets one FAILED kind, never a ready one.
    failedKind () {
      const artifacts = (this.envelope && this.envelope.artifacts) || []
      const failed = artifacts.filter(a => a.status === 'failed')
      return failed.length > 0 ? failed[0].kind : null
    },
    measureText () {
      const el = this.$refs.text
      if (!el) {
        this.textClamped = false
        return
      }
      this.textClamped = el.scrollHeight > el.clientHeight
    }
  }
}
</script>

<style scoped>
.sg-panel {
  direction: rtl;
  text-align: right;
  width: 100%;
  margin: 0 0 4px;
  font-family: inherit;
  font-size: 13px;
}

/* ---- C3.1 quiet row: exactly 28px in every non-expanded state ---- */
.sg-row {
  display: flex;
  align-items: center;
  gap: 8px;
  height: 28px;
  padding: 0;
  margin: 0;
  border: 0;
  background: transparent;
  font-size: 13px;
  line-height: 28px;
  color: #6c757d;
}
.sg-row.is-ready .sg-text-line {
  color: #2c3e50;
  font-weight: 500;
}
.sg-row .sg-text-line {
  unicode-bidi: plaintext;
}
.sg-row.is-wrap {
  height: auto;
  min-height: 28px;
  line-height: 1.5;
  padding: 4px 0;
  align-items: flex-start;
}
.sg-marker {
  flex: 0 0 6px;
  height: 6px;
  border-radius: 50%;
  border: 1px solid #adb5bd;
  background: transparent;
}
.sg-marker.is-filled {
  background: #28a745;
  border-color: #28a745;
}
.sg-check {
  font-size: 11px;
  color: #28a745;
}

.sg-link {
  background: none;
  border: 0;
  padding: 0 2px;
  border-radius: 2px;
  font: inherit;
  font-size: 13px;
  color: #3273dc;
  cursor: pointer;
}
.sg-link:hover {
  text-decoration: underline;
}
.sg-link:focus-visible {
  outline: 2px solid #3273dc;
  outline-offset: 2px;
}
.sg-link[aria-disabled='true'] {
  color: #adb5bd;
  cursor: default;
  text-decoration: none;
  pointer-events: none;
}

/* ---- C3.2 expanded card ---- */
.sg-card {
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  background: #fafafa;
  padding: 10px 12px;
  max-height: 168px;
  overflow-y: auto;
  animation: sg-fade 120ms ease-out;
}
@keyframes sg-fade {
  from { opacity: 0; }
  to { opacity: 1; }
}
.sg-caption {
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 20px;
  font-size: 12px;
  font-weight: 600;
  color: #6c757d;
}
.sg-label {
  margin: 4px 0 0;
  font-size: 12px;
  font-weight: 400;
  color: #6c757d;
}
.sg-divider {
  border-top: 1px solid #e9ecef;
  margin: 8px 0;
}
.sg-text {
  font-size: 13px;
  line-height: 1.6;
  color: #333333;
  white-space: pre-line;
  max-height: 125px;
  overflow-y: auto;
  unicode-bidi: plaintext;
  text-align: right;
  -webkit-user-select: text;
  user-select: text;
}
.sg-text.is-full {
  max-height: none;
}
.sg-text-more {
  display: block;
  text-align: left;
  margin-top: 4px;
  font-size: 12px;
}

/* ---- C3.3 action row ---- */
.sg-actions {
  display: flex;
  align-items: center;
  gap: 12px;
  height: 34px;
  margin-top: 8px;
}
.sg-btn {
  height: 32px;
  padding: 0 14px;
  border-radius: 4px;
  border: 1px solid transparent;
  font-family: inherit;
  font-size: 13px;
  font-weight: 600;
  line-height: 1;
  cursor: pointer;
}
.sg-btn:focus-visible {
  outline: 2px solid #3273dc;
  outline-offset: 2px;
}
.sg-btn.is-primary {
  background: #3273dc;
  color: #ffffff;
}
.sg-btn.is-primary:hover {
  background: #2760bd;
}
.sg-btn.is-secondary {
  background: #ffffff;
  color: #3273dc;
  border-color: #3273dc;
}
.sg-btn.is-secondary:hover {
  background: #f0f7ff;
}
.sg-btn.is-ghost {
  height: 30px;
  background: transparent;
  color: #6c757d;
  border-color: #ced4da;
  font-weight: 500;
}
.sg-btn.is-ghost:hover {
  background: #f1f3f5;
}
.sg-btn[disabled] {
  opacity: 0.5;
  pointer-events: none;
}

/* ---- C3.4 inline confirm ---- */
.sg-confirm {
  background: #fff3cd;
  border: 1px solid #ffc107;
  border-radius: 6px;
  padding: 8px 10px;
  margin-top: 8px;
  direction: rtl;
  text-align: right;
}
.sg-confirm.is-standalone {
  margin-top: 6px;
}
.sg-confirm-msg {
  font-size: 13px;
  line-height: 1.5;
  color: #856404;
  font-weight: 500;
  margin: 0 0 8px;
  unicode-bidi: plaintext;
}
.sg-confirm-row {
  display: flex;
  gap: 8px;
  justify-content: flex-end;
}
.sg-confirm .sg-btn.is-primary {
  height: 30px;
}
.sg-panel.is-confirming .sg-text {
  max-height: 72px;
}

@media (prefers-reduced-motion: reduce) {
  .sg-card {
    animation: none;
  }
}
</style>
